import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

import { CargoCompanyUsersTable, AssignUserModal } from "../../components/backend/cargocompany";
import {
  useCargoCompany,
  useCargoCompanyUsers,
  useCargoCompanyMutations,
} from "../../hooks/useCargoCompany";
import { CargoCompanyUser } from "../../types/cargoCompany";
import { formatApiError } from "../../utils/formatApiError";
import AdminPage from "./AdminPage";

const CargoCompanyUsersPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    user?: CargoCompanyUser;
  }>({
    open: false,
  });

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  // Data fetching
  const {
    data: companyData,
    isLoading: isLoadingCompany,
    error: companyError,
  } = useCargoCompany(id || "", !!id);

  const {
    data: usersData,
    isLoading: isLoadingUsers,
    error: usersError,
    refetch: refetchUsers,
  } = useCargoCompanyUsers(id || "", true, !!id);

  // Mutations
  const { assignUser, removeUser } = useCargoCompanyMutations();

  const company = companyData?.data;
  const users = usersData?.data || [];
  const assignedUserIds = users.map((u) => u.userId);

  const handleAssignUser = async (userId: string) => {
    if (!id) return;

    try {
      await assignUser.mutateAsync({
        id,
        data: {
          userId,
          cargoCompanyId: id,
        },
      });
      setNotification({
        open: true,
        message: t("admin.cargoCompanyUsers.assignSuccess"),
        severity: "success",
      });
      setAssignModalOpen(false);
      refetchUsers();
    } catch (error) {
      throw error; // Let the modal handle the error
    }
  };

  const handleRemoveUser = (user: CargoCompanyUser) => {
    setDeleteDialog({ open: true, user });
  };

  const confirmRemoveUser = async () => {
    if (!deleteDialog.user || !id) return;

    try {
      await removeUser.mutateAsync({
        id,
        userId: deleteDialog.user.userId,
      });
      setNotification({
        open: true,
        message: t("admin.cargoCompanyUsers.removeSuccess"),
        severity: "success",
      });
      setDeleteDialog({ open: false });
      refetchUsers();
    } catch (error) {
      setNotification({
        open: true,
        message: formatApiError(error),
        severity: "error",
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const isLoadingMutation = assignUser.isPending || removeUser.isPending;

  if (!id) {
    return (
      <AdminPage>
        <Alert severity="error">{t("admin.cargoCompanyUsers.invalidCompanyId")}</Alert>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <Box>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate("/admin/cargo-companies")}
            sx={{ cursor: "pointer", textDecoration: "none" }}
          >
            {t("admin.cargoCompanyUsers.breadcrumbCompanies")}
          </Link>
          <Typography color="text.primary">
            {company?.name || t("admin.cargoCompanyUsers.loading")} - {t("admin.cargoCompanyUsers.users")}
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/admin/cargo-companies")}
            >
              {t("admin.cargoCompanyUsers.back")}
            </Button>
            <Box>
              <Typography variant="h4">
                {company?.name || t("admin.cargoCompanyUsers.loading")} - {t("admin.cargoCompanyUsers.users")}
              </Typography>
              {company && (
                <Typography variant="body2" color="text.secondary">
                  {t("admin.cargoCompanyUsers.codeLabel")}: {company.code} | {t("admin.cargoCompanyUsers.usersAssigned", { count: users.length })}
                </Typography>
              )}
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAssignModalOpen(true)}
            disabled={isLoadingCompany}
          >
            {t("admin.cargoCompanyUsers.assignUser")}
          </Button>
        </Box>

        {/* Error States */}
        {companyError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("admin.cargoCompanyUsers.loadError")}
          </Alert>
        )}

        {/* Users Table */}
        <CargoCompanyUsersTable
          users={users}
          isLoading={isLoadingUsers}
          error={usersError}
          onRemove={handleRemoveUser}
        />

        {/* Assign User Modal */}
        <AssignUserModal
          open={assignModalOpen}
          cargoCompanyId={id}
          assignedUserIds={assignedUserIds}
          onClose={() => setAssignModalOpen(false)}
          onAssign={handleAssignUser}
          isLoading={assignUser.isPending}
          error={assignUser.error}
        />

        {/* Remove User Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false })}
        >
          <DialogTitle>{t("admin.cargoCompanyUsers.removeTitle")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("admin.cargoCompanyUsers.removeConfirmation", { name: `${deleteDialog.user?.userFirstName} ${deleteDialog.user?.userLastName}`, email: deleteDialog.user?.userEmail })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialog({ open: false })}
              disabled={isLoadingMutation}
            >
              {t("admin.cargoCompanyUsers.cancel")}
            </Button>
            <Button
              onClick={confirmRemoveUser}
              color="error"
              variant="contained"
              disabled={isLoadingMutation}
              startIcon={
                isLoadingMutation ? <CircularProgress size={20} /> : null
              }
            >
              {isLoadingMutation ? t("admin.cargoCompanyUsers.removing") : t("admin.cargoCompanyUsers.remove")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminPage>
  );
};

export default CargoCompanyUsersPage;

